/*!
* @copyright
* Copyright(c) 2018 SICK AG
*/
import {
  Director,
  Stage,
  DefaultCaps,
  CapDataDesc,
  StageDesc,
  DefaultNavigators,
  SceneCompass,
} from '@sick-davinci/glow';
import {
  PolarScanDesc,
  PolarScanDecorationDesc,
  CrownViewPresentAddDesc,
  CrownViewCapData,
  CrownScanCap,
  ChannelType,
  ColorMapType,
} from '@sick-davinci/glow-crown';
import {
  CrownConnectionProxy,
  CrownProtocolJSON,
} from '@sick-davinci/device-communication';
import { AxesHelper, Vector3 } from 'three';
import * as dat from 'three/examples/js/libs/dat.gui.min.js';

const controller = {
  noise: 50,
  pointSize: 5,
  useRssiColorMap: true,
  rssiInterpolation: 'LINEAR',
  rssiStartColor: [0, 0, 255],
  rssiCenterColor: [0, 255, 255],
  rssiStopColor: [255, 0, 0],
  defaultColor: [255, 0, 0],
};
/**
 * Demo:    CrownScanViewer
 * Description:
 *   This demo application shows how to visualizing a CrownPolaScan.
 *   Scan data is simulated.
 * Features:
 *  - Orbit navigation with custom settings
 *  - Capability handling
 *  - Scene compass
 *  - ViewCrown / Proxy (offline) connection
 */
export const run = (async (viewParent: HTMLElement) => {
  createGUI();

  // ** setup the stage *
  const setup: StageDesc = {
    cameras: [{
      name: 'camera',
      from: [8700, 14000, 16000],
      up: [0, 0, 1],
      near: 40,
      far: 400000,
      fov: 30,
    }],
    scenes: [{
        name: 'default',
      }],
    viewports: [
      {
        name: 'default',
        scene: 'default',
        camera: 'camera',
        navigator: 'orbit',
        bgcolor: 0x393939,
      },
    ],
  };

  const stage = new Stage(viewParent, setup);

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit', {
    rotateAround: {
      up: new Vector3(0, 0, 1),
    },
    straightAhead: {
      speed: 800, // speed up is necessary because scan coordinates are quite big
    }, pan: {
      speed: 400,
    },
  });

  // * Add the scene compass for better orientation
  SceneCompass.create(stage, 'default', 'camera');

  // * Start displaying the scene
  stage.start();

  // * Create the axis which is used to rotate the camera about
  stage.applyThreeDirect((scene) => {
    const axesHelper = new AxesHelper(1000);
    axesHelper.name = 'axes';
    scene.add(axesHelper);
  });

  // * Setup the device connection.
  // * In this case it is a proxy connection which simulates establishing the connection to the device so CrownView will work *
  const connectionProxy = new CrownConnectionProxy((json) => {
    const data = JSON.parse(json);
    if (!!data && !!data.header && !!data.header.function) {
      const func = data.header.function;
      switch (func) {
        case 'View/Present/create': // simulate registration (called by CrownView)
          connectionProxy.putObj(JSON.stringify({ header: { status: 0 }, data: { handle: { type: 'handle', id: 0 } } }));
          break;
        case 'View/Present/setID':
          connectionProxy.putObj(JSON.stringify({ header: { status: 0 } }));
          break;
        case 'View/Present/register':
          connectionProxy.putObj(JSON.stringify({ header: { status: 0 }, data: { success: true } }));
          break;
        default: break;
      }
    }
  });

  // Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director); // add default caps
  director.addCapability(new CrownScanCap());

  // ** Set all necessary callback functions for the device connection
  const view = new CrownViewCapData(connectionProxy, new CrownProtocolJSON(), 'default');
  view.onPresentRemove((name) => {
    const objectId = stage.getObjectIdByName(name);
    stage.clearObject(objectId);
  });

  view.onPresentClear(() => {
    stage.clearObject(stage.getObjectIdByName('scan'));
  });

  view.onPresentAddCapData((capData) => {
    // usually the dist data and decoration is comming from the device and should not be modified by the viewer!
    // just to add some animation :-)
    const deco = capData.payload.Decorations[0].data as PolarScanDecorationDesc;
    const colorMap = deco.ColorMaps[0].ColorMap;

    (<CapDataDesc<CrownViewPresentAddDesc<PolarScanDesc>>> capData).payload.Iconics.forEach( (d) => {
      const distDataArray = d.data.DistValues[0].data;
      const distData = new Float32Array(distDataArray.buffer);

      distData.forEach((dist, i) => distData[i] = dist - ((Math.random() - 0.5) * controller.noise));

      deco.PointSize = controller.pointSize;

      if (controller.useRssiColorMap) {
        colorMap.ColorMapType = controller.rssiInterpolation as ColorMapType;
        colorMap.ColorStops[0].R = controller.rssiStartColor[0];
        colorMap.ColorStops[0].G = controller.rssiStartColor[1];
        colorMap.ColorStops[0].B = controller.rssiStartColor[2];

        colorMap.ColorStops[1].R = controller.rssiCenterColor[0];
        colorMap.ColorStops[1].G = controller.rssiCenterColor[1];
        colorMap.ColorStops[1].B = controller.rssiCenterColor[2];

        colorMap.ColorStops[2].R = controller.rssiStopColor[0];
        colorMap.ColorStops[2].G = controller.rssiStopColor[1];
        colorMap.ColorStops[2].B = controller.rssiStopColor[2];
      } else {
        deco.ColorMaps = new Array();
        deco.Colors = new Array();
        deco.Colors.push({
          R: controller.defaultColor[0],
          G: controller.defaultColor[1],
          B: controller.defaultColor[2],
          Echo: 1,
          ChannelType: ChannelType.ALL_CHANNELS,
          ChannelTypeKey: null,
        });
      }
    });

    capData.name = 'scan';
    director.handleCapData(capData);
  });

  // * load and send a stored scan data to the crown connection (usually this comes from a device)
  const scan = await fetch('./resources/scan.json')
    .then((response) => response.text());
  setInterval(() => connectionProxy.putObj(scan), 100);
});

function createGUI() {
  const gui = new dat.GUI();
  gui.close();
  gui.add(controller, 'noise', 0.0, 100, 1);
  gui.add(controller, 'pointSize', 1, 10, 1);
  gui.add(controller, 'useRssiColorMap');
  gui.add(controller, 'rssiInterpolation', ['LINEAR', 'DISCRETE']);
  gui.addColor(controller, 'rssiStartColor');
  gui.addColor(controller, 'rssiCenterColor');
  gui.addColor(controller, 'rssiStopColor');
  gui.addColor(controller, 'defaultColor');
}
